import { type ComponentProps, type ReactNode } from 'react';
import { Radio, RadioGroup, Label, Description } from '@heroui/react';
import { cn } from '../utils';

interface SelectableTileProps {
  value: string;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  isDisabled?: boolean;
  className?: string;
}

export const SelectableTile = ({
  value,
  title,
  description,
  icon,
  isDisabled,
  className
}: SelectableTileProps) => (
  <Radio value={value} isDisabled={isDisabled}>
    <Radio.Content
      className={cn(
        'group relative flex cursor-pointer flex-col gap-3 rounded-2xl border-2 border-default-200 bg-content2/60 p-4 text-left',
        'transition-[transform,border-color,background-color,box-shadow] duration-150',
        'hover:border-default-300',
        'data-[selected=true]:border-(--ark-color-accent) data-[selected=true]:bg-[color-mix(in_oklab,var(--ark-color-accent)_16%,transparent)]',
        'data-[focus-visible=true]:outline-none data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-(--ark-color-accent)',
        'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-60',
        className
      )}>
      {({ isSelected }) => (
        <>
          <span
            className={cn(
              'absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full text-[11px] transition',
              isSelected
                ? 'scale-100 bg-(--ark-color-accent) text-[#1a1206] opacity-100'
                : 'scale-50 opacity-0'
            )}
            aria-hidden="true">
            ✓
          </span>
          {icon && (
            <span
              className={cn(
                'grid h-10 w-10 place-items-center rounded-xl transition',
                isSelected
                  ? 'bg-(--ark-color-accent) text-[#1a1206]'
                  : 'bg-content3 text-default-600'
              )}>
              {icon}
            </span>
          )}
          <span className="block text-sm font-semibold text-foreground">{title}</span>
          {description && (
            <span className="block text-xs leading-relaxed text-default-500">{description}</span>
          )}
        </>
      )}
    </Radio.Content>
  </Radio>
);

type TileGroupProps = Omit<ComponentProps<typeof RadioGroup>, 'children'> & {
  label?: ReactNode;
  description?: ReactNode;
  gridClassName?: string;
  children?: ReactNode;
};

export const TileGroup = ({
  label,
  description,
  gridClassName,
  className,
  children,
  ...props
}: TileGroupProps) => (
  <RadioGroup className={cn('gap-2', className)} {...props}>
    {label && <Label className="text-sm font-medium">{label}</Label>}
    {description && <Description>{description}</Description>}
    <div className={cn('mt-1 grid grid-cols-1 gap-3 sm:grid-cols-2', gridClassName)}>
      {children}
    </div>
  </RadioGroup>
);
